/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { fmtMinP, rateColor, BRAND_TEAL } from './spacerLogUtils';

const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];

const SpacerLogHeatmap = ({ days = [], fromDate, toDate, onDayClick }) => {
  const [tooltip, setTooltip] = useState('Hover a day to see its numbers.');

  const dayMap = useMemo(() => {
    const map = {};
    (days || []).forEach((d) => {
      map[d.date] = d;
    });
    return map;
  }, [days]);

  const weeks = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const start = moment(fromDate);
    const end = moment(toDate);
    let gridStart = start.clone().startOf('isoWeek');
    const gridEnd = end.clone().endOf('isoWeek');
    const result = [];
    let cursor = gridStart.clone();
    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      const week = [];
      for (let i = 0; i < 7; i += 1) {
        week.push(cursor.clone());
        cursor = cursor.add(1, 'day');
      }
      result.push(week);
    }
    return result;
  }, [fromDate, toDate]);

  const renderCell = (d) => {
    const key = d.format('YYYY-MM-DD');
    const inRange = d.isSameOrAfter(fromDate, 'day') && d.isSameOrBefore(toDate, 'day');
    if (!inRange) {
      return <div key={key} className="spacer-log-heat-cell spacer-log-heat-cell--empty" />;
    }
    const rec = dayMap[key];
    if (!rec || !rec.hasLog) {
      return (
        <div
          key={key}
          className="spacer-log-heat-cell spacer-log-heat-cell--nodata"
          onMouseEnter={() => setTooltip(`${key} — no log data`)}
        />
      );
    }
    if (rec.skipped) {
      return (
        <div
          key={key}
          role="button"
          tabIndex={0}
          className="spacer-log-heat-cell spacer-log-heat-cell--skipped spacer-log-heat-cell--clickable"
          onClick={() => onDayClick?.(key)}
          onKeyDown={(e) => e.key === 'Enter' && onDayClick?.(key)}
          onMouseEnter={() => setTooltip(`${key} — shift skipped, no production logged`)}
        />
      );
    }
    const rate = rec.completionRatePct ?? 0;
    return (
      <div
        key={key}
        role="button"
        tabIndex={0}
        className="spacer-log-heat-cell spacer-log-heat-cell--clickable"
        style={{ background: rateColor(rate) }}
        onClick={() => onDayClick?.(key)}
        onKeyDown={(e) => e.key === 'Enter' && onDayClick?.(key)}
        onMouseEnter={() =>
          setTooltip(
            `${key} — ${rec.jointsCompleted}/${rec.jointsLogged} joints complete (${rate}%) · ${fmtMinP(rec.downtimeMinutes)} downtime · ${rec.avgCycleTimeMinutes ?? '—'} min avg cycle`
          )
        }
      />
    );
  };

  return (
    <div>
      <div className="spacer-log-heatmap-wrap">
        <div className="spacer-log-heat-row">
          <div className="spacer-log-heat-daylabels">
            {DAY_LABELS.map((l, i) => (
              <div key={`dl-${i}`} className="spacer-log-heat-daylabel">
                {l}
              </div>
            ))}
          </div>
          <div className="spacer-log-heatmap">
            {weeks.map((week, wi) => (
              <div key={`w-${wi}`} className="spacer-log-heat-week">
                {week.map((d) => renderCell(d))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="spacer-log-heat-legend">
        <span>Low</span>
        <div className="spacer-log-heat-legend-scale">
          <div className="spacer-log-heat-swatch" style={{ background: '#C4001F' }} />
          <div className="spacer-log-heat-swatch" style={{ background: '#BD5730' }} />
          <div className="spacer-log-heat-swatch" style={{ background: '#B38600' }} />
          <div className="spacer-log-heat-swatch" style={{ background: BRAND_TEAL }} />
        </div>
        <span>High</span>
        <span className="spacer-log-heat-legend-gap">
          <span className="spacer-log-legend-swatch spacer-log-legend-swatch--muted" /> no data
        </span>
        <span>▨ shift skipped (no production)</span>
      </div>
      <div className="spacer-log-heat-tooltip">{tooltip}</div>
    </div>
  );
};

export default SpacerLogHeatmap;
